import { Overlay } from '@angular/cdk/overlay';
import { AsyncPipe, KeyValue, NgClass } from '@angular/common';
import {
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnInit,
    Output,
    ViewChild,
    booleanAttribute,
    forwardRef
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import {
    MAT_LEGACY_AUTOCOMPLETE_SCROLL_STRATEGY as MAT_AUTOCOMPLETE_SCROLL_STRATEGY,
    MatLegacyAutocomplete as MatAutocomplete,
    MatLegacyAutocompleteModule
} from '@angular/material/legacy-autocomplete';
import {
    MatLegacyCheckboxChange as MatCheckboxChange,
    MatLegacyCheckboxModule
} from '@angular/material/legacy-checkbox';
import { MatLegacyChipsModule } from '@angular/material/legacy-chips';
import { MatLegacyFormFieldModule } from '@angular/material/legacy-form-field';
import { MatLegacyInputModule } from '@angular/material/legacy-input';
import { BehaviorSubject } from 'rxjs';
import { map, startWith } from 'rxjs/operators';

export function scrollFactory(overlay: Overlay): () => any {
    return () => overlay.scrollStrategies.close();
}

@Component({
    selector: 'autocomplete-select-all-multiselect',
    templateUrl: './autocomplete-select-all-multiselect.component.html',
    styleUrls: ['./autocomplete-select-all-multiselect.component.scss'],
    providers: [
        {
            provide: MAT_AUTOCOMPLETE_SCROLL_STRATEGY,
            useFactory: scrollFactory,
            deps: [Overlay]
        },
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => AutocompleteSelectAllMultiselectComponent),
            multi: true
        }
    ],
    standalone: true,
    imports: [
        NgClass,
        MatLegacyFormFieldModule,
        MatLegacyChipsModule,
        MatLegacyAutocompleteModule,
        MatLegacyCheckboxModule,
        ReactiveFormsModule,
        MatLegacyInputModule,
        AsyncPipe,
        MatIconModule
    ]
})
export class AutocompleteSelectAllMultiselectComponent implements ControlValueAccessor, OnInit {
    @ViewChild('autoInput') autoInput: ElementRef;
    @ViewChild('input') input: ElementRef;
    @ViewChild('auto') autocomplete: MatAutocomplete;

    @Input()
    placeholder = '';

    readonly selectAllValue: KeyValue<number | string, string> = { key: -1, value: 'Select All' };

    selectAllSelected = false;

    userControl = new UntypedFormControl();

    focused = false;

    searchValue: string;

    _options: Array<KeyValue<string | number, string>> = [];

    @Input({ transform: booleanAttribute })
    startsWith = false;

    @Input({ transform: booleanAttribute })
    clearOnselect = false;

    @Input()
    width: string;

    @Input()
    showSelectAll = true;

    @Input()
    set options(value: Array<KeyValue<string | number, string>>) {
        this.selectAllSelected = false;

        this.selection = [];

        this._options = value ? value : [];
        this.userControl.setValue(this.lastFilter);
    }

    @Input()
    selection: Array<KeyValue<string | number, string>> = new Array<KeyValue<string | number, string>>();

    @Output()
    selectionChange: EventEmitter<Array<KeyValue<string | number, string>>> = new EventEmitter<
        Array<KeyValue<string | number, string>>
    >();

    filteredItems: BehaviorSubject<Array<KeyValue<string | number, string>>> = new BehaviorSubject<
        Array<KeyValue<string | number, string>>
    >([]);

    lastFilter = '';

    ngOnInit() {
        this.userControl.valueChanges
            .pipe(
                startWith<string | Array<KeyValue<string, string>>>(''),
                map((value) => (typeof value === 'string' ? value : this.lastFilter)),
                map((filter) => this.filter(filter))
            )
            .subscribe((e) => {
                return this.filteredItems.next(e);
            });
    }

    filter(filter: string): Array<KeyValue<string | number, string>> {
        this.lastFilter = filter;
        if (filter) {
            return this._options.filter((option) => {
                if (this.startsWith) {
                    return option.value.toLowerCase().startsWith(filter.toLowerCase());
                } else {
                    return option.value.toLowerCase().indexOf(filter.toLowerCase()) >= 0;
                }
            });
        } else {
            return this._options.slice();
        }
    }

    optionClicked(event: Event, user: KeyValue<string | number, string>) {
        event.stopPropagation();
        this.toggleSelection(user, true);
    }

    toggleSelection(user: KeyValue<string | number, string>, clear = false) {
        const selected = !this.isSelected(user);

        this.handleClearOnSelect(clear);

        if (selected) {
            this.setSelection([...this.selection, user]);

            if (this.selection.length === this._options.length) {
                this.selectAllSelected = true;
            }

            this.filteredItems.next(
                this.filteredItems.value.map((e) => {
                    if (e.key === user.key) {
                        return { ...e, selected: true };
                    }

                    return e;
                })
            );
        } else {
            this.setSelection(this.selection.filter((value) => value.key.toString() !== user.key.toString()));

            this.selectAllSelected = false;

            this.filteredItems.next(
                this.filteredItems.value.map((e) => {
                    if (e.key === user.key) {
                        return { ...e, selected: false };
                    }

                    return e;
                })
            );
        }
    }

    handleClearOnSelect(selected: boolean) {
        if (selected && this.clearOnselect) {
            const newvalue = '';

            this.userControl.setValue(newvalue);

            this.input.nativeElement.value = newvalue;
        }
    }

    isSelected(option: KeyValue<string | number, string>) {
        return this.selection.some((e) => e.key.toString() === option.key.toString());
    }

    selectAllClick($event: MouseEvent | MatCheckboxChange) {
        if ($event instanceof MouseEvent) {
            $event.stopPropagation();
        }

        this.handleClearOnSelect(true);

        if (this.selection.length === this._options.length) {
            this.selectAllSelected = false;
            this.setSelection([]);

            this.filteredItems.next(
                this.filter(this.lastFilter).map((e) => {
                    return { ...e, selected: false };
                })
            );
        } else {
            this.selectAllSelected = true;

            this.setSelection(
                this._options.slice().map((e) => {
                    return { ...e, selected: true };
                })
            );

            this.filteredItems.next(
                this.filter(this.lastFilter).map((e) => {
                    return { ...e, selected: true };
                })
            );
        }
    }

    setSelection(value: Array<KeyValue<string | number, string>>) {
        this.selection = value;
        this.selectionChange.emit(value);

        this.writeValue(value);
    }

    onChange: any = (value) => {};
    onTouched: any = () => {};

    registerOnChange(fn: any) {
        this.onChange = fn;
    }

    registerOnTouched(fn: any) {
        this.onTouched = fn;
    }

    writeValue(value: Array<KeyValue<string | number, string>>) {
        this.selection = value;

        this.onChange(value);
    }

    setDisabledState(_: boolean) {}

    opened = () => {
        if (this.width) {
            return;
        }

        const inputWidth = this.autoInput.nativeElement.getBoundingClientRect().width - 12;

        setTimeout(() => {
            const panel = this.autocomplete.panel?.nativeElement;

            if (!panel) {
                return;
            }

            panel.style.width = `${inputWidth}px`;
        });
    };
}
